<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import slugify from 'slugify'

import useMedia from '@/composables/media'
import useSessionStorage from '@/composables/sessionStorage'
import { useContentScopeEntry } from '@/composables/contentScope'
import { isContentScope } from '@/utils/contentScope'
import { useChannel } from '@/views/contests/composables/channel'

import OrderedSteps from '@/components/OrderedSteps.vue'
import ContestShareView from './Components/ContestShareView.vue'
import ContestUpsell from './Components/ContestUpsell.vue'
import ContestsTabAppearance from './Components/ContestsTabAppearance.vue'
import ContestsTabGlobal from './Components/ContestsTabGlobal.vue'
import ContestsTabReward from './Components/ContestsTabReward.vue'

import {
  MAX_CONTEST_REWARDS,
  calculateTotalProbability,
  capitalizeFirstLetter,
  isContestMechanic,
  roundToDecimals,
  withEveryDaySlotProbability
} from './utils'

import type { Contest, ContestGameReward, LoyaltyReward } from '@/types/types'

interface Boost {
  id: string
  title: string
  description: string
}

interface Step {
  id: string
  title: string
  isComplete: boolean
  href: string
  stepOrder: number
  icon?: any
}

interface MessageChannel {
  id: number
  slug: string
}

interface Store {
  storeId: string | number
  storeUrlId?: number
  shortLink?: {
    shortLink?: string
  }
}

interface StoreUrl {
  id: number
  storeUrlCategoryId: number
}

interface StoreData {
  id: number
  slug: string
  urls?: StoreUrl[]
}

interface WheelReward {
  id: number
  bgColor: string
  color: string
  value: string
  probability: number
}

interface CategoriesIds {
  facebookId: number | null
  googleId: number | null
  instagramId: number | null
}

const boostBaseTranslation = 'pages.contests.contestsCreatePage.global.form.fields.boost'
const boosts: Boost[] = [
  {
    id: 'none',
    title: `${boostBaseTranslation}.none.title`,
    description: `${boostBaseTranslation}.none.description`
  },
  {
    id: 'custom',
    title: `${boostBaseTranslation}.custom.title`,
    description: `${boostBaseTranslation}.custom.description`
  },
  {
    id: 'facebook',
    title: `${boostBaseTranslation}.facebook.title`,
    description: `${boostBaseTranslation}.facebook.description`
  },
  {
    id: 'instagram',
    title: `${boostBaseTranslation}.instagram.title`,
    description: `${boostBaseTranslation}.instagram.description`
  },
  {
    id: 'google',
    title: `${boostBaseTranslation}.google.title`,
    description: `${boostBaseTranslation}.google.description`
  }
]

const route = useRoute()
const router = useRouter()
const notification = useNotification()
const { send } = useEvent()
const { getContrast } = useColor()
const { merchantName, user, merchantLogo, isSupportConnection } = useLocalStorage()
const { duplicateContestItem, isEmptyDuplicateContestItem } = useSessionStorage()
const { getStoreById, getStoresByPermission, getStoreSchedulingPlatformUrl, selectedStore } = useStore()
const { hasModuleBySlug } = useAccess()
const { getBase64, isDefaultLogo } = useMedia()
const { isUrlValid } = useRegex()
const { t } = useI18n()
const { fetchAllRewards } = useReward()

const selectedBoost = ref<Boost>(boosts[0])
const selectedPlayableFrequency = ref<any>()
const defaultLogo = ref<string | null>(null)
const steps = ref<Step[]>([
  {
    id: 'prices',
    title: t('pages.contests.contestsCreatePage.tabs.prices'),
    isComplete: false,
    href: '#',
    stepOrder: 1
  },
  {
    id: 'style',
    title: t('pages.contests.contestsCreatePage.tabs.style'),
    isComplete: false,
    href: '#',
    stepOrder: 2
  },
  {
    id: 'global',
    title: t('pages.contests.contestsCreatePage.tabs.global'),
    isComplete: false,
    href: '#',
    stepOrder: 3
  }
])
const currentTab = ref(1)
const loaded = ref(false)
const queryScope = isContentScope(route.query.scope) ? route.query.scope : undefined

const contest = ref<Contest>({
  name: '',
  slug: '',
  scope: queryScope,
  isVisibleInWebapp: !!route.query?.activeInWebApp,
  fromDate: null,
  toDate: null,
  coverImage: null,
  logo: null,
  title: t('pages.contests.contestsCreatePage.preview.title'),
  subtitle: t('pages.contests.contestsCreatePage.preview.subtitle'),
  askActionTitle: t('pages.contests.contestsCreatePage.preview.askActionTitle.none'),
  askActionText: t('pages.contests.contestsCreatePage.preview.askActionText'),
  askActionButtonText: t('pages.contests.contestsCreatePage.preview.askActionButtonText'),
  canLose: false,
  losingProbability: 0,
  losingTileColor: '#BFBFBF',
  losingTileText: t('pages.contests.contestsCreatePage.preview.losingTileText'),
  losingText: t('pages.contests.contestsCreatePage.preview.losingText'),
  winningText: t('pages.contests.contestsCreatePage.preview.winningText'),
  callToActionText: t('pages.contests.contestsCreatePage.preview.callToActionText'),
  formTitle: t('pages.contests.contestsCreatePage.preview.formTitle'),
  formSubtitle: t('pages.contests.contestsCreatePage.preview.formSubtitle'),
  formSubmitButtonText: t('pages.contests.contestsCreatePage.preview.formSubmitButtonText'),
  optinText: t('pages.contests.contestsCreatePage.preview.optinText', { merchantName: merchantName.value }),
  contestGameRewards: [],
  isPlayableManyTimes: false,
  daysBetweenParticipations: 0,
  isClickRequiredToPlay: false,
  messageContentPoints: '',
  messageTemplate: {
    content: '',
    subject: t('pages.contests.contestsCreatePage.preview.messageTemplate.subject'),
    messageChannelId: 1
  },
  stores: [],
  losingNotificationText: t('pages.contests.contestsCreatePage.preview.messageTemplate.losingNotificationText'),
  sendingNotificationText: t('pages.contests.contestsCreatePage.preview.messageTemplate.sendingNotificationText.sms'),
  waitingValidationText: t('pages.contests.contestsCreatePage.preview.messageTemplate.waitingValidationText'),
  targetUrl: null,
  isOtpRequired: false,
  isBotDetectionEnabled: false,
  migratedFromContestGameId: null
})

const categoriesIds = ref<CategoriesIds>({
  facebookId: null,
  googleId: null,
  instagramId: null
})
const sumPercentage = ref(0)
const coverSizeOk = ref(true)
const logoSizeOk = ref(true)
const invalidCharacters = ref({
  reward: false,
  sms: false,
  message: false
})
const error = ref<string | null>(null)
const channels = ref<MessageChannel[]>([])
const dateRangeValue = ref<string[]>([])

const hasDefaultAppearanceSettings = ref(false)
const hasDefaultGlobalSettings = ref(false)

const { updateChannel } = useChannel(contest)

const parent = computed(() =>
  isEditMode.value
    ? { label: contest.value.name, to: { name: 'ContestDetail', params: { contestId: contest.value.id } } }
    : { label: t('pages.contests.contestsPage.title'), to: { name: 'ContestList' } }
)

const submitButtonText = computed(() => {
  if (!isEditMode.value && currentTab.value < 3) {
    return t('global.words.next')
  }
  if (currentTab.value === 4) {
    return t('pages.contests.sharePage.finishButton')
  }
  return t(
    !isEditMode.value
      ? 'pages.contests.contestsCreatePage.buttons.createContestGame'
      : 'pages.contests.contestsCreatePage.buttons.update'
  )
})

const computedRewards = computed(() => {
  const rewards: WheelReward[] = []
  contest.value.contestGameRewards.forEach((reward, index) => {
    rewards.push({
      id: index + 1,
      bgColor: reward.color,
      color: reward.textColor,
      value: reward.name,
      probability: reward.probability
    })
  })
  return rewards
})

const { isScopeModalOpen, confirmScope, askScope } = useContentScopeEntry(
  computed({
    get: () => contest.value.scope,
    set: (value) => {
      contest.value.scope = value ?? undefined
    }
  })
)

const merchantId = computed(() => user.value.merchant.id)

const isEditMode = computed(() => Number(route.params?.contestId) > 0)

watch(loaded, (isLoaded) => {
  if (isLoaded && !isEditMode.value) askScope()
})

const contestSlug = computed(() => {
  const merchantNameAsSlug = slugify(merchantName.value, {
    lower: true,
    strict: true,
    locale: 'fr'
  })
  const contestSlugValue = slugify(contest.value.name, {
    lower: true,
    strict: true,
    locale: 'fr'
  })
  return `${merchantNameAsSlug}-${contestSlugValue}`
})

const checkDisabledButton = computed(() => {
  const isMail = channelType.value === 'mail'
  const checkRewards: boolean[] = []
  // Les emojis sont autorisés en email ; on ne valide les caractères que pour les canaux GSM (SMS/push)
  if (!isMail) {
    checkAllowedCharacters(contest.value.messageTemplate.content, 'message')
    contest.value.contestGameRewards.forEach((reward) => {
      checkRewards.push(/[^\p{L}\p{N}\p{P}\p{Z}\p{M}^$€<>=+¨~−\n]/gu.test(reward.notificationTextOverride || ''))
    })
  }
  const hasInvalidMessage = !isMail && invalidCharacters.value.message
  const contestIsInvalid = checkRewards.includes(true) || hasInvalidMessage || !checkSumPercentage.value
  return contestIsInvalid || !hasModuleBySlug('game-contest')
})

const checkSumPercentage = computed(() => {
  calculateSumPercentage()
  return isEditMode.value ? sumPercentage.value === 100 : true
})

const channelType = computed(() => {
  return channels.value.find((c) => c.id === contest.value.messageTemplate.messageChannelId)?.slug
})

const messageMaxLength = computed(() => (channelType.value === 'mail' ? 10000 : 160))

const mainTitle = computed(() => {
  if (isEditMode.value) {
    return 'pages.contests.contestsEditPage.title'
  }
  return templateId.value
    ? 'pages.contests.contestsCreatePage.titleWithTemplate'
    : 'pages.contests.contestsCreatePage.title'
})

const templateId = computed(() => route.query?.templateId)

const presetMechanic = computed(() => (isContestMechanic(route.query?.mechanic) ? route.query.mechanic : null))

const shouldOpenRewardSelection = ref(!isEditMode.value && !!route.query?.selectRewards)

const libraryRewards = ref<LoyaltyReward[]>([])
const isLoadingLibraryRewards = ref(false)

const loadLibraryRewards = async () => {
  isLoadingLibraryRewards.value = true
  try {
    libraryRewards.value = (await fetchAllRewards()) ?? []
  } finally {
    isLoadingLibraryRewards.value = false
  }
}

const fileName = computed(() => {
  const isSingleStore = selectedStore.value !== 0
  const name = `${contest.value.slug}${isSingleStore ? `-${getStoreById(selectedStore.value).slug}` : ''}`
  return `pongo-contest-${name}.png`
})

const getShortLink = computed(() => {
  if (contest.value.stores.length === 1) {
    return contest.value?.storesShortLinks ? contest.value.storesShortLinks[0]?.shortLink?.shortLink : ''
  }
  return selectedStore.value === 0
    ? contest.value.shortLink?.shortLink
    : (
        contest.value.stores.filter(
          (store) => typeof store === 'object' && 'storeId' in store && store.storeId === selectedStore.value
        )[0] as Store
      )?.shortLink?.shortLink
})

const contactUrl = computed(() => {
  const url = `${getStoreSchedulingPlatformUrl(
    selectedStore.value
  )}?utm_source=pongo&utm_medium=dashboard&utm_campaign=dashboard&utm_content=demo`
  return url
})

const isShareViewVisible = computed(() => route.params?.success === 'success')

const emitSelectedBoost = (item: Boost) => {
  selectedBoost.value = item
}

const emitSelectedFrequency = (item: any) => {
  selectedPlayableFrequency.value = item
}

const preventDefaultSubmit = (e?: Event) => {
  e?.preventDefault()
}

const forwardSteps = async (e: number) => {
  if (steps.value[e - 1]?.isComplete) {
    steps.value[e - 1].isComplete = false
  }
  await checkContestSettings(e)

  if (isEditMode.value && error.value) {
    notification.create('error', {
      text: error.value
    })
  }
}

const generateContest = (contestData: Contest) => {
  contest.value = contestData
  if (isEditMode.value) {
    contest.value.storesShortLinks = contest.value.stores as Store[]
  }

  const stores: number[] = []
  contest.value.stores.forEach((store) => {
    const storeId = typeof store === 'object' && 'storeId' in store ? store.storeId : store
    stores.push(Number(storeId))
  })
  contest.value.stores = stores

  contest.value.migratedFromContestGameId =
    contestData.migratedFromContestGameId ?? contestData.migratedFromContestGame?.id ?? null

  createMessageChannelId()
  formatDate()
  generateLosingProbability()
  findSelectedBoost()
}

const generateImg = async () => {
  try {
    if (contest.value.coverImageUrl) {
      contest.value.coverImage = await getBase64(contest.value.coverImageUrl)
    }
    if (contest.value?.logoUrl) {
      contest.value.logo = await getBase64(contest.value.logoUrl)
      return
    }
    if (merchantLogo.value) {
      defaultLogo.value = await getBase64(merchantLogo.value)
      contest.value.logo = defaultLogo.value
    }
  } catch {
    loaded.value = true
  }
}

const createMessageChannelId = () => {
  if (contest.value.messageTemplate.messageChannel) {
    contest.value.messageTemplate.messageChannelId = contest.value.messageTemplate.messageChannel.id
  }
}

const formatDate = () => {
  const dates: string[] = []

  if (contest.value.fromDate) {
    dates[0] = dayjs(new Date(contest.value.fromDate)).format('DD/MM/YYYY')
  }
  if (contest.value.toDate) {
    dates[1] = dayjs(new Date(contest.value.toDate)).format('DD/MM/YYYY')
  }

  if (dates.length > 0) {
    dateRangeValue.value = dates
    dateRangeChanged(dates)
  }
}

const generateLosingProbability = () => {
  contest.value.losingProbability = 100
  contest.value.contestGameRewards.forEach((reward) => {
    contest.value.losingProbability -= reward.probability
  })
  contest.value.losingProbability = roundToDecimals(contest.value.losingProbability)

  contest.value.canLose = contest.value.losingProbability > 0
  if (!contest.value.canLose) {
    contest.value.losingProbability = 25
  }

  if (contest.value.canLose && !contest.value.contestGameRewards.some((reward) => reward.rewardTypeSlug === 'lost')) {
    contest.value.contestGameRewards?.push({
      name: contest.value.losingTileText,
      probability: contest.value.losingProbability,
      color: contest.value.losingTileColor,
      textColor: contest.value.losingTileTextColor,
      notificationTextOverride: '',
      points: 0,
      rewardTypeSlug: 'lost'
    })
  }
}

const findSelectedBoost = () => {
  const found = boosts.find((boost) => boost.id === contest.value.contestGameBoostType?.slug)
  if (found) {
    selectedBoost.value = found
    contest.value.isClickRequiredToPlay = selectedBoost.value.id !== 'none'
  }
}

const hasGoogle = (store: StoreData) => {
  return store?.urls?.map((url) => url.storeUrlCategoryId === categoriesIds.value.googleId)?.includes(true)
}

const hasFacebook = (store: StoreData) => {
  return store?.urls?.map((url) => url.storeUrlCategoryId === categoriesIds.value.facebookId)?.includes(true)
}

const hasInstagram = (store: StoreData) => {
  return store?.urls?.map((url) => url.storeUrlCategoryId === categoriesIds.value.instagramId)?.includes(true)
}

const isStoreAvailable = (storeId: string | number): boolean => {
  if (['none', 'custom'].includes(selectedBoost.value.id)) {
    return true
  }

  const store = getStoreById(storeId as number) as StoreData
  if (selectedBoost.value.id === 'facebook' && !hasFacebook(store)) {
    return false
  }
  if (selectedBoost.value.id === 'instagram' && !hasInstagram(store)) {
    return false
  }
  if (selectedBoost.value.id === 'google' && !hasGoogle(store)) {
    return false
  }
  return true
}

const fetchCategoriesUrl = async () => {
  return axios.get('/store-url-categories').then((response) => {
    const categories = response.data.data
    categoriesIds.value.facebookId = categories.filter((category: any) => category.slug === 'facebook')[0].id
    categoriesIds.value.googleId = categories.filter((category: any) => category.slug === 'google')[0].id
    categoriesIds.value.instagramId = categories.filter((category: any) => category.slug === 'instagram')[0].id
  })
}

const fetchDefaultImages = async () => {
  return axios.get(`/merchants/${merchantId.value}/media`).then((response) => {
    contest.value.defaultCover = response.data.data.coverA
  })
}

const fetchData = async (withCategoriesUrl = false) => {
  const { data } = await axios.get('/message-channels')
  channels.value = data.data

  const { data: dataMerchant } = await axios.get(`/merchants/${merchantId.value}/colors`)
  if (!isEditMode.value && isEmptyDuplicateContestItem.value) {
    const colors = dataMerchant.data
    contest.value.backgroundColor = contest.value.backgroundColor ?? colors.background
    contest.value.buttonColor = getContrast(contest.value.backgroundColor) ?? getContrast(colors.background)
    contest.value.textColor = getContrast(contest.value.backgroundColor) ?? getContrast(colors.background)
    contest.value.buttonTextColor = contest.value.backgroundColor ?? colors.background
    contest.value.losingTileTextColor = contest.value.losingTileTextColor ?? colors.text
  }

  if (withCategoriesUrl) {
    await fetchCategoriesUrl()
  }
}

const calculateSumPercentage = () => {
  sumPercentage.value = calculateTotalProbability(computedRewards.value)
}

const dateRangeChanged = (newDateRangeValue: string[]) => {
  contest.value.fromDate = newDateRangeValue[0] ?? null
  contest.value.toDate = newDateRangeValue[1] ?? null
}

const checkAllowedCharacters = (value: string, element: string) => {
  invalidCharacters.value[element as keyof typeof invalidCharacters.value] =
    /[^\p{L}\p{N}\p{P}\p{Z}\p{M}^$€<>=+¨~−\n]/gu.test(value)
}

const isInt = (value: any): boolean => {
  return !isNaN(value) && parseInt(value) == value && !isNaN(parseInt(value, 10))
}

const removeAllNotificationTextOverride = () => {
  contest.value.contestGameRewards.forEach((reward) => (reward.notificationTextOverride = null))
}

const checkErrors = async (currentStep: number) => {
  error.value = null
  await checkRewardsError()

  await checkContestSettings(currentStep)
  checkEmptyFields()
  checkCanLoseErrors()
  checkMessageTemplate()
  checkCustomizableFrequency()
  checkGlobalSettings()
}

const checkCustomizableFrequency = () => {
  if (selectedPlayableFrequency.value?.id === 'customize' && Number(contest.value.daysBetweenParticipations) < 2) {
    error.value = t('pages.contests.contestsCreatePage.notifications.error.customizeDays')
  }
}

const checkEmptyFields = () => {
  const fieldsToCheck = [
    'name',
    'stores',
    'title',
    'subtitle',
    'callToActionText',
    'askActionTitle',
    'askActionText',
    'askActionButtonText',
    'waitingValidationText',
    'winningText',
    'losingText',
    'losingTileText',
    'sendingNotificationText',
    'optinText',
    'formTitle',
    'formSubtitle',
    'formSubmitButtonText'
  ] as const

  for (const field of fieldsToCheck) {
    const value = contest.value[field as keyof Contest]
    if (Array.isArray(value) ? value.length === 0 : !value || (value as string).length === 0) {
      error.value = t(`pages.contests.contestsCreatePage.notifications.error.${field}`)
      return
    }
  }
}

const checkCanLoseErrors = () => {
  if (!contest.value.canLose) {
    return
  }

  const fieldsToCheck = ['losingProbability', 'losingTileText', 'losingText'] as const
  for (const field of fieldsToCheck) {
    const value = contest.value[field]
    if (!value || (typeof value === 'string' && value.length === 0)) {
      error.value = t(`pages.contests.contestsCreatePage.notifications.error.${field}`)
      return
    }
  }
}

const checkMessageTemplate = () => {
  if (contest.value.messageTemplate.messageChannelId === null) {
    error.value = t('pages.contests.contestsCreatePage.notifications.error.communicationChannel')
  } else if (contest.value.messageTemplate.content?.length === 0) {
    error.value = t('pages.contests.contestsCreatePage.notifications.error.messageTemplate')
  } else if (channelType.value === 'sms' && contest.value.messageTemplate.content?.length > 160) {
    error.value = t('pages.contests.contestsCreatePage.notifications.error.messageTemplateLength', { nb: 160 })
  } else if (channelType.value === 'mail' && contest.value.messageTemplate.subject?.length === 0) {
    error.value = t('pages.contests.contestsCreatePage.notifications.error.messageTemplateMailObject')
  }
}

const getRewardWithTooLongNotificationText = async (rewards: ContestGameReward[]) => {
  let rewardWithTooLongText: ContestGameReward[] = []

  await Promise.all(
    rewards.map(async (reward) => {
      if (reward.notificationTextOverride) {
        const messageLength = await axios.get('contest-games/message-length', {
          params: {
            message: reward.notificationTextOverride,
            rewardName: reward.name,
            points: reward.points || 0,
            type: channelType.value
          }
        })

        if (messageLength.data.data.messageLength > messageMaxLength.value) {
          rewardWithTooLongText.push(reward)
        }
      }
    })
  )

  return rewardWithTooLongText
}

const checkRewardsError = async (): Promise<boolean> => {
  const rewards = contest.value.contestGameRewards
  const contestGameRewardsLength = rewards.length

  const getError = (errorKey: string, params?: any) => {
    error.value = t(`pages.contests.contestsCreatePage.notifications.error.${errorKey}`, params)
  }

  if (contestGameRewardsLength < 2 || contestGameRewardsLength > MAX_CONTEST_REWARDS) {
    getError('maximumRewardsCount', { from: 2, to: MAX_CONTEST_REWARDS })
    return false
  }

  const hasRewardNotSelected = rewards.some((reward) => reward.rewardTypeSlug === 'free_product' && !reward.rewardId)
  if (hasRewardNotSelected) {
    getError('rewardNotSelected')
    return false
  }

  const hasEmptyName = rewards.some((reward) => reward.name.length === 0)
  if (hasEmptyName) {
    getError('priceName')
    return false
  }

  const hasInvalidPoints = rewards.some((reward) => reward.points !== null && !isInt(reward.points || 0))
  if (hasInvalidPoints) {
    getError('rewardPointsNumber')
    return false
  }

  const rewardNameMaxLength = 32

  const hasLongRewardName = rewards.some((reward) => reward.name.length > rewardNameMaxLength)
  if (hasLongRewardName) {
    getError('rewardNameLength', { nb: rewardNameMaxLength })
    return false
  }

  const hasNoProbability = rewards.some((reward) => !reward.probability)
  if (hasNoProbability) {
    getError('priceProbability')
    return false
  }

  if (sumPercentage.value !== 100) {
    getError('probabilityNot100')
    return false
  }

  let rewardWithTooLongText = await getRewardWithTooLongNotificationText(rewards)

  if (rewardWithTooLongText.length > 1) {
    getError('rewardNotificationLengthWithNamesPlural', {
      rewards: rewardWithTooLongText
        .map((reward) => `"${reward.name}"`)
        .join(', ')
        .replace(/, (?=[^,]*$)/, ` ${t('global.words.and')} `),
      nb: messageMaxLength.value
    })
    return false
  }

  if (rewardWithTooLongText.length === 1) {
    getError('rewardNotificationLengthWithNames', {
      name: `"${rewardWithTooLongText[0].name}"`,
      nb: messageMaxLength.value
    })
    return false
  }

  return true
}

const checkGlobalSettings = () => {
  if (contest.value.conditionsUrl && !isUrlValid(contest.value.conditionsUrl)) {
    error.value = t('pages.contests.contestsCreatePage.notifications.error.conditionsUrl')
    return false
  }

  if (selectedBoost.value?.id === 'custom') {
    if (!contest.value.targetUrl) {
      error.value = t('pages.contests.contestsCreatePage.notifications.error.customLink')
      return false
    }

    if (!isUrlValid(contest.value.targetUrl)) {
      error.value = t('pages.contests.contestsCreatePage.notifications.error.targetUrl')
      return false
    }
  }

  // Check if all selected stores the user has access to are available with the selected boost
  if (selectedBoost.value?.id && !['none', 'custom'].includes(selectedBoost.value.id)) {
    const permittedStoreIds = getStoresByPermission('contest-game.create').map((s) => s.id)
    const unavailableStores = contest.value.stores.filter((storeId) => {
      return permittedStoreIds.includes(storeId) && !isStoreAvailable(storeId)
    })

    if (unavailableStores.length > 0) {
      error.value = t('pages.contests.contestsCreatePage.global.form.infos.selectedStoresNotAvailable', {
        count: unavailableStores.length,
        boostType: capitalizeFirstLetter(selectedBoost.value.id)
      })
      return false
    }
  }

  return true
}

const checkContestSettings = async (step: number) => {
  error.value = null
  const hasRewardsError = await checkRewardsError()

  let stepIsComplete
  switch (currentTab.value) {
    case 1:
      stepIsComplete = hasRewardsError
      if (stepIsComplete) {
        steps.value[0].isComplete = true
        currentTab.value = step
      } else {
        return true
      }
      break
    case 2:
      if (isDefaultLogo(merchantLogo.value) && !isEditMode.value && defaultLogo.value === contest.value.logo) {
        notification.create('error', {
          text: t('pages.contests.contestsCreatePage.notifications.error.logo')
        })
        return true
      } else {
        steps.value[1].isComplete = true
        currentTab.value = step
      }
      break
    case 3:
      currentTab.value = step

      break
    case 4:
      currentTab.value = step
      break
  }

  return false
}

const save = async () => {
  if (!isEditMode.value && currentTab.value < 3) {
    const stepHasError = await checkContestSettings(currentTab.value)
    if (stepHasError) {
      notification.create('error', {
        text: error.value,
        timeout: 5000
      })
    } else {
      currentTab.value++
    }

    return
  }

  if (isShareViewVisible.value) {
    router.push({
      name: 'ContestDetail',
      params: { contestId: contest.value.id }
    })
    return
  }

  await checkErrors(currentTab.value)

  if (contest.value.fromDate === null && contest.value.toDate === null) {
    contest.value.fromDate = dayjs().format('DD/MM/YYYY')
    contest.value.toDate = '01/01/3000'
  }

  if (error.value) {
    notification.create('error', {
      text: error.value,
      timeout: 5000
    })
    return
  }

  if (templateId.value) {
    send('segment', 'contest.template:create:clicked', {
      templateId: Number(templateId.value),
      contestId: user.value.merchant.publicId,
      module: 'contest'
    })
  }

  const stores = contest.value.stores.map((storeId) => {
    const formattedStore: any = {
      storeId: storeId
    }

    if (['facebook', 'instagram', 'google'].includes(selectedBoost.value.id)) {
      const store = getStoreById(storeId as number) as StoreData
      const categoryId = `${selectedBoost.value.id}Id` as keyof CategoriesIds
      const selectedBoostCategoryId = categoriesIds.value[categoryId]

      return {
        ...formattedStore,
        storeUrlId: store?.urls?.filter((url) => url.storeUrlCategoryId === selectedBoostCategoryId)[0]?.id
      }
    }

    return formattedStore
  })

  if (stores.length === 0) {
    notification.create('error', {
      text: t('pages.contests.contestsCreatePage.notifications.error.stores'),
      timeout: 5000
    })
    return
  }

  if (contest.value.isGeneric) {
    removeAllNotificationTextOverride()
  }

  const losingReward = contest.value.contestGameRewards.find((reward) => reward.rewardTypeSlug === 'lost')
  const params = {
    name: contest.value.name,
    slug: contestSlug.value,
    ...(contest.value.scope && { scope: contest.value.scope }),
    conditionsUrl: contest.value.conditionsUrl,
    title: contest.value.title,
    subtitle: contest.value.subtitle,
    callToActionText: contest.value.callToActionText,
    askActionTitle: contest.value.askActionTitle,
    askActionText: contest.value.askActionText,
    askActionButtonText: contest.value.askActionButtonText,
    waitingValidationText: contest.value.waitingValidationText,
    winningText: contest.value.winningText,
    losingText: contest.value.losingText,
    losingNotificationText: losingReward?.notificationTextOverride,
    losingTileColor: losingReward?.color,
    losingTileTextColor: losingReward?.textColor,
    losingTileText: losingReward?.name ?? t('pages.contests.contestsCreatePage.preview.losingTileText'),
    sendingNotificationText: contest.value.sendingNotificationText,
    optinText: contest.value.optinText,
    formTitle: contest.value.formTitle,
    formSubtitle: contest.value.formSubtitle,
    formSubmitButtonText: contest.value.formSubmitButtonText,
    backgroundColor: contest.value.backgroundColor,
    textColor: contest.value.textColor,
    buttonColor: contest.value.buttonColor,
    buttonTextColor: contest.value.buttonTextColor,
    coverImage: contest.value.coverImage,
    logo: contest.value.logo,
    stores: stores,
    contestGameRewards: contest.value.contestGameRewards
      .filter((reward) => reward.rewardTypeSlug !== 'lost')
      .map((reward) => ({
        ...withEveryDaySlotProbability(reward),
        minimumOrderAmount: Number(reward.minimumOrderAmount) * 100 || 0,
        value: Number(reward.value) * 100 || 0,
        points: Number(reward.points) || 0
      })),
    contestGameBoostType: selectedBoost.value.id,
    messageTemplate: contest.value.messageTemplate,
    isPlayableManyTimes: contest.value.isPlayableManyTimes,
    daysBetweenParticipations: parseInt(contest.value.daysBetweenParticipations.toString(), 10),
    isClickRequiredToPlay: contest.value.isClickRequiredToPlay,
    targetUrl: contest.value.targetUrl,
    fromDate: dayjs(contest.value.fromDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
    toDate: dayjs(contest.value.toDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
    isEnabled: true,
    isVisibleInWebapp: contest.value.isVisibleInWebapp,
    mechanic: contest.value.mechanic,
    isOtpRequired: contest.value.isOtpRequired ?? false,
    isBotDetectionEnabled: contest.value.isBotDetectionEnabled ?? false,
    ...(isSupportConnection.value && {
      migratedFromContestGameId: contest.value.migratedFromContestGameId ?? null
    })
  }

  loaded.value = false

  if (!isEditMode.value) {
    try {
      const response = await axios.post('/contest-games', params)
      notification.create('success', {
        text: t('pages.contests.contestsCreatePage.notifications.create.success')
      })
      contest.value = response.data.data
      router.push({
        name: 'ContestEdit',
        params: { contestId: contest.value.id, success: 'success' }
      })
    } catch (e: any) {
      const textError =
        e.response?.data?.errors?.slug !== null
          ? Object.values(e.response.data.errors)[0][0]
          : t('pages.contests.contestsCreatePage.notifications.create.error')

      notification.create('error', {
        text: textError as string,
        persist: true
      })
    } finally {
      loaded.value = true
    }
  } else {
    try {
      const response = await axios.put(`/contest-games/${route.params.contestId}`, params)
      notification.create('success', {
        text: t('pages.contests.contestsEditPage.notifications.create.success')
      })
      const contestId = response.data.data.id
      router.push({
        name: 'ContestDetail',
        params: { contestId: contestId }
      })
    } catch (e: any) {
      const textError =
        e.response?.data?.errors?.slug !== null
          ? Object.values(e.response.data.errors)[0][0]
          : t('pages.contests.contestsEditPage.notifications.create.error')

      notification.create('error', {
        text: textError as string,
        persist: true
      })
    } finally {
      loaded.value = true
    }
  }
}

const fetchTemplate = async () => {
  const { data } = await axios.get(`templates/${templateId.value}?module=contests`)
  const templatableData = data.data.templatable

  contest.value = {
    ...contest.value,
    ...templatableData
  }

  const contestGameRewards = templatableData.contestGameRewards.map((reward: any) => ({
    ...reward,
    rewardTypeSlug: reward.rewardType.slug
  }))

  contest.value.contestGameRewards = contestGameRewards
  contest.value.contestGameBoostType = {
    slug: templatableData.contestGameBoostType.slug
  }
  const boostSlug = contest.value.contestGameBoostType.slug
  const askActionTitleKey = `pages.contests.contestsCreatePage.preview.askActionTitle.${boostSlug}`
  const askActionTitle = t(askActionTitleKey)
  contest.value.askActionTitle =
    askActionTitle === askActionTitleKey
      ? t('pages.contests.contestsCreatePage.preview.askActionTitle.custom')
      : askActionTitle

  generateLosingProbability()
  findSelectedBoost()
}

watch(selectedBoost, () => {
  contest.value.isClickRequiredToPlay = selectedBoost.value?.id !== 'none'
  if (['none', 'custom'].includes(selectedBoost.value?.id)) {
    return true
  }
  if (selectedBoost.value?.id !== 'custom') {
    contest.value.targetUrl = null
  }
})

onUnmounted(() => {
  duplicateContestItem.value = {}
})

const init = async () => {
  if (isShareViewVisible.value) {
    currentTab.value = 4
  }

  if (isEditMode.value) {
    steps.value.push({
      id: 'launch',
      title: t('pages.contests.contestsCreatePage.tabs.launch'),
      isComplete: false,
      href: '#',
      stepOrder: 4
    })

    const response = await axios.get(`/contest-games/${route.params.contestId}`)
    generateContest(response.data.data)
    await fetchCategoriesUrl()
    await fetchData(false)
    loaded.value = true
  } else {
    if (shouldOpenRewardSelection.value) loadLibraryRewards()

    await fetchDefaultImages()
    await fetchData(true)

    if (templateId.value) {
      await fetchTemplate()
    }

    if (!isEmptyDuplicateContestItem.value) {
      generateContest(duplicateContestItem.value as Contest)
      findSelectedBoost()
    }

    if (presetMechanic.value) {
      contest.value.mechanic = presetMechanic.value
    }

    await generateImg()
    loaded.value = true
  }
}

const updateContest = (updatedContest: Contest) => {
  contest.value = updatedContest
}

init()
updateChannel()
</script>

<template>
  <div>
    <form v-if="loaded" @submit.prevent="preventDefaultSubmit()">
      <TheDetailHeader :title="t(mainTitle)" :parent="parent">
        <template v-if="templateId || contest.scope || isEditMode" #badge>
          <BaseChip v-if="templateId" color="yellow" :text="t('pages.survey.surveyCreatePage.labels.template')" />
          <ContentScopeDetailBadge
            v-if="contest.scope || isEditMode"
            v-model:scope="contest.scope"
            subject="contest_game"
            :editable="!isEditMode"
          />
        </template>
        <template #actions>
          <Button
            type="button"
            variant="default-primary"
            :disabled="checkDisabledButton"
            class="fixed bottom-0 left-0 z-30 w-screen rounded-none py-4 sm:static sm:w-auto sm:rounded-control sm:py-2"
            @click="save()"
          >
            {{ submitButtonText }}
          </Button>
          <ContestUpsell v-if="!hasModuleBySlug('game-contest')" />
        </template>
      </TheDetailHeader>
      <div v-if="!coverSizeOk" class="mx-6 mb-9 rounded-md bg-yellow-50 p-4 sm:mx-12">
        <div class="flex">
          <div class="shrink-0">
            <BaseIcon name="HeroInformationCircleIconSolid" class="w-5 text-yellow-400" />
          </div>
          <div class="ml-3 flex-1 md:flex md:justify-between">
            <p class="text-sm text-yellow-700">
              {{ $t('pages.contests.contestsCreatePage.infos.coverImageError') }}
            </p>
            <p v-if="currentTab !== 2" class="mt-3 text-sm md:ml-6 md:mt-0">
              <button
                type="button"
                class="whitespace-nowrap font-medium text-primary hover:text-primary/80"
                @click="currentTab = 2"
              >
                {{ $t('pages.contests.contestsCreatePage.buttons.backToStyleTab') }}
                <span aria-hidden="true">&rarr;</span>
              </button>
            </p>
          </div>
        </div>
      </div>
      <div v-if="!logoSizeOk" class="mb-9 rounded-md bg-yellow-50 p-4">
        <div class="flex">
          <div class="shrink-0">
            <BaseIcon name="HeroInformationCircleIconSolid" class="w-5 text-yellow-400" />
          </div>
          <div class="ml-3 flex-1 md:flex md:justify-between">
            <p class="text-sm text-yellow-700">
              {{ $t('pages.contests.contestsCreatePage.infos.logoError') }}
            </p>
            <p v-if="currentTab !== 2" class="mt-3 text-sm md:ml-6 md:mt-0">
              <button
                type="button"
                class="whitespace-nowrap font-medium text-primary hover:text-primary/80"
                @click="currentTab = 2"
              >
                {{ $t('pages.contests.contestsCreatePage.buttons.backToStyleTab') }}
                <span aria-hidden="true">&rarr;</span>
              </button>
            </p>
          </div>
        </div>
      </div>
      <div class="px-6 pb-8 sm:px-12">
        <OrderedSteps
          v-show="!isShareViewVisible"
          :steps="steps"
          :current-step="currentTab"
          :has-step-number="true"
          @update:current-step="forwardSteps($event)"
        />
      </div>

      <div v-if="currentTab === 1" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestsTabReward
          :get-contest="contest"
          :is-edit-mode="isEditMode"
          :auto-open-reward-selection="shouldOpenRewardSelection"
          :library-rewards="libraryRewards"
          :is-loading-library-rewards="isLoadingLibraryRewards"
          @reward-selection-shown="shouldOpenRewardSelection = false"
          @refresh-library-rewards="loadLibraryRewards"
          @update:modelValue="updateContest"
        />
      </div>
      <div v-if="currentTab === 2" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestsTabAppearance
          v-model:has-default-settings="hasDefaultAppearanceSettings"
          :get-contest="contest"
          :selected-boost="selectedBoost"
        />
      </div>
      <div v-if="currentTab === 3" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestsTabGlobal
          v-model:has-default-settings="hasDefaultGlobalSettings"
          v-model:selected-boost="selectedBoost"
          :categories-ids="categoriesIds"
          :is-edit-mode="isEditMode"
          :get-contest="contest"
          :is-template-mode="!!templateId"
          @selected-boost="emitSelectedBoost"
          @selected-frequency="emitSelectedFrequency"
        />
      </div>
      <div v-if="currentTab === 4" class="px-6 pb-6 sm:px-12 sm:pb-12">
        <ContestShareView :short-link="getShortLink" :file-name="fileName" :contact-url="contactUrl" />
      </div>
    </form>
    <TheLoadingPage v-else />
    <ContentScopeModal
      v-model:open="isScopeModalOpen"
      subject="contest_game"
      @confirm="confirmScope"
      @cancel="router.push({ name: 'ContestList' })"
    />
  </div>
</template>
